import HalfPriceTraffic from "@components/HalfPriceTraffic";
import Modal from "@components/ui/Modal";
import { prevUrlBeforAuth } from "@store/reducers/auth";
import Close from "assets/svg/close.svg";
import Calender from "assets/svg/details/property/calender.svg";
import Degree from "assets/svg/details/property/degree.svg";
import Hourglass from "assets/svg/details/property/hourglass.svg";
import Progress from "assets/svg/details/property/progress.svg";
import Play from "assets/svg/play.svg";
import SnappPayIcon from "assets/svg/snapp-pay.svg";
import SnappPayMiniIcon from "assets/svg/snapp-pay-mini.svg";
import Alert from "assets/svg/student/alert-exit.svg";
import { Button } from "components";
import gate from "gate";
import Cookies from "js-cookie";
import Head from "next/head";
import { useRouter } from "next/router";
import React, { FC, useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { commafy, convertMiladiToJalali, loadState } from "src/utils";

import { CampaignType } from "../../../../enums/campaign-type";
import style from "../styles.module.css";
import Title from "../Title";

type TypeDate = {
  start: string;
  end: string;
  releaseDate: string;
};
type Props = {
  image: string;
  imageAlternateText: string;
  releaseDate: string;
  titleData: { name: string; description: string };
  price: number;
  holdingType: string;
  prerequisitesRef: any;
  discount: number;
  isBuyable: boolean;
  badge?: string;
  video: {
    url: string;
    name?: string;
    description?: string;
    date?: string;
    isSchema?: boolean;
  };
  date: TypeDate;
  courseDuration: string;
  certificate: {
    certificateTitle: string;
    hasCertificate: string;
  };
  courseType: number;
  isFree: boolean;
  id: number | string;
  extraDescription: string;
  allowSnapPay: boolean;
  snappPayInstallment: number;
};

const Index: FC<Props> = ({
  image,
  certificate,
  courseDuration,
  courseType,
  date,
  holdingType,
  prerequisitesRef,
  discount,
  price,
  imageAlternateText,
  titleData,
  video,
  isBuyable,
  badge,
  isFree,
  id,
  extraDescription,
  allowSnapPay,
  snappPayInstallment,
}) => {
  const [isShowVideoModal, setIsShowVideoModal] = useState({
    isShow: false,
    url: "",
  });
  const [isShowModal, setIsShowModal] = useState(false);
  const dispatch = useDispatch();
  const query = useQueryClient();
  const state = useSelector((state: any) => state.auth);
  const router = useRouter();
  const [isSnapPayAvailable, setIsSnapPayAvailable] = useState<{
    text: string;
    icon: string;
  } | null>(null);
  const { mutate: createUserActivityMutate } = useMutation(
    gate.createUserActivity
  );
  const getDateByData = (
    date: TypeDate
  ): { data: string | undefined; type: "date" | "releaseDate" } => {
    if (date?.start) {
      return date?.start == date?.end
        ? { data: convertMiladiToJalali(date?.start), type: "date" }
        : {
            data: `${convertMiladiToJalali(
              date?.start
            )} تا ${convertMiladiToJalali(date?.end)}`,
            type: "date",
          };
    }
    return {
      data: convertMiladiToJalali(date?.releaseDate),
      type: "releaseDate",
    };
  };
  const { mutate: checkValidForSnapp, isLoading: isLoadingForValidSnapp } =
    useMutation(gate.isValidForSnapPay, {
      onSuccess: (data: any) => {
        setIsSnapPayAvailable({
          text: data?.data?.response?.description,
          icon: data?.data?.logo,
        });
      },
      onError: () => {
        toast.error("متاسفانه عملیات با مشکل مواجه شد");
      },
    });
  const parseSnappPayInstallmentAmount = (
    description?: string
  ): number | null => {
    if (!description) return null;

    const persianDigits: Record<string, string> = {
      "۰": "0",
      "۱": "1",
      "۲": "2",
      "۳": "3",
      "۴": "4",
      "۵": "5",
      "۶": "6",
      "۷": "7",
      "۸": "8",
      "۹": "9",
    };

    const match = description.match(/ماهانه\s+([\d۰-۹,٬]+)\s*تومان/);
    if (!match || !match[1]) return null;

    const normalized = match[1]
      .split("")
      .map((ch) => (persianDigits[ch] !== undefined ? persianDigits[ch] : ch))
      .join("")
      .replace(/[٬,]/g, "");

    const value = Number(normalized);
    return Number.isNaN(value) ? null : value;
  };
  const { mutate, isLoading } = useMutation(gate.addOrder, {
    onSuccess: (data: any) => {
      state?.prevUrl &&
        dispatch(prevUrlBeforAuth({ path: "", needRedirect: false }));
      if (data?.data?.isSuccessful) {
        query.invalidateQueries(["order-list"]);
        toast.success(
          price == 0
            ? "این دوره به داشبورد شما اضافه شد"
            : "این سفارش به سبد خرید شما اضافه شد"
        );
      } else {
        setIsShowModal(true);
      }
      let campaign: any = localStorage.getItem("campaign");
      if (campaign && JSON.parse(campaign)) {
        campaign = JSON.parse(campaign);
        createUserActivityMutate({
          campaignId: +campaign.campaign_id,
          followUpId: +campaign.followup_id,
          phoneNumber: campaign.phone_number,
          type: +CampaignType.AddToBasket,
          url: campaign.url,
          isPromotion: campaign?.isPromotion.toLowerCase() === "true",
        });
      }
    },
    onError: (data: any) => {
      state?.prevUrl &&
        dispatch(prevUrlBeforAuth({ path: "", needRedirect: false }));
      if (!data?.handleGlobally) {
        toast.error(data?.data?.message);
      }
    },
  });
  useEffect(() => {
    if (allowSnapPay) {
      checkValidForSnapp(
        discount !== null && discount !== 0 ? discount : price
      );
    }
  }, [allowSnapPay, price, discount, checkValidForSnapp]);

  const installmentPriceFactor =
    discount !== null && discount !== 0 && price ? discount / price : 1;

  const getCurrentInstallmentPrice = (basePrice: any) => {
    const value = Number(basePrice ?? 0);
    if (!installmentPriceFactor || installmentPriceFactor === 1) {
      return value;
    }
    return Math.round(value * installmentPriceFactor);
  };
  const onPlayVideo = () => {
    console.log("");
  };
  const handlePay = () => {
    if (!Cookies.get("access_token")) {
      dispatch(prevUrlBeforAuth({ path: router.asPath, needRedirect: false }));
      router.push("/auth");
    } else {
      mutate({
        price,
        productType: 0,
        isInstallment: false,
        productId: +id,
        discount: discount ? discount : 0,
      });
    }
  };

  useEffect(() => {
    const persistState = loadState();
    if (
      date &&
      persistState?.auth?.prevUrl &&
      persistState?.auth?.needRedirect
    ) {
      // !Cookies.get("access_token")
      //   ? dispatch(prevUrlBeforAuth(""))
      //   :
      handlePay();
    } else {
      dispatch(prevUrlBeforAuth({ path: "", needRedirect: false }));
    }
  }, []);

  return (
    <>
      {video?.url && (
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: `{
                    "@context": "https://schema.org",
                    "@type": "VideoObject",
                    "thumbnailUrl":"https://dadbanan.com/images/logo-white.png",
                    "name": "${
                      (video?.name as string)?.replace(/"/g, " ") ?? "ویدیو"
                    }",
                    "description": "${
                      (video?.description as string)?.replace(/"/g, " ") ??
                      (video?.name as string)?.replace(/"/g, " ")
                    }" ,
                    "uploadDate": "${video?.date ?? Date.now()}",
                    "contentUrl": "${video?.url}",
                    "interactionStatistic": {
                        "@type": "InteractionCounter",
                        "interactionType": { "@type": "WatchAction" }
                    },
                    "regionsAllowed": "IR"
              }`,
            }}
          />
        </Head>
      )}
      <Modal
        visible={isShowVideoModal.isShow}
        onClose={() => setIsShowVideoModal({ isShow: false, url: "" })}
        className="h-screen w-screen"
      >
        <div className="relative mx-auto h-60 w-10/12 rounded bg-black md:h-96 md:w-8/12 xl:h-190">
          <video
            controls
            src={video?.url}
            autoPlay
            className="h-full w-full"
            onPlay={onPlayVideo}
            onPause={onPlayVideo}
          />
          <Close
            onClick={() => setIsShowVideoModal({ isShow: false, url: "" })}
            className="absolute top-5 left-5 cursor-pointer fill-white"
          />
          <HalfPriceTraffic
            classes="absolute top-[1rem] right-[1rem] z-50 rounded"
            type="VIDEO"
            spaces="pr-2 md:pr-4 !ml-2"
            text="هزینه اینترنت مصرفی : نیم‌بها"
          />
        </div>
      </Modal>
      <Modal
        visible={isShowModal}
        onClose={() => setIsShowModal(false)}
        className="h-screen w-screen"
      >
        <div className="relative mx-auto flex h-40 w-10/12 flex-col justify-between rounded bg-white py-6 px-4 md:h-[189px] md:w-[418px] md:p-[30px] xl:h-[189px]">
          <div className="flex gap-x-4">
            <Alert />

            <p className="self-center text-xs md:text-base">
              برای خرید این دوره ابتدا باید پیش نیازهای این دوره خریداری گردد.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-x-2 text-white">
            <Button
              onClick={() => setIsShowModal(false)}
              className="w-full rounded bg-[#B8B7B7] py-2 transition-all hover:ring-2"
            >
              انصراف
            </Button>
            <Button
              className="w-full rounded bg-[#EB7A33] py-2 text-white transition-all hover:ring-2"
              onClick={() => {
                prerequisitesRef?.current?.scrollIntoView(true);
                setIsShowModal(false);
              }}
            >
              جزئیات پیش نیاز
            </Button>
          </div>
        </div>
      </Modal>
      <div
        className="-top-[17rem] h-auto w-full rounded lg:relative lg:w-[80%] 2xl:w-[65%]"
        style={{ contentVisibility: "auto" }}
      >
        <div className="relative rounded bg-white pb-10 shadow-md">
          {video?.url && (
            <div className="relative mb-7 h-[164px] w-full bg-black/20 md:h-auto xl:h-auto">
              <video
                src={video?.url}
                className="h-auto w-full object-contain"
              />

              <Play
                onClick={() =>
                  setIsShowVideoModal({ isShow: true, url: video?.url })
                }
                className="absolute top-1/2 left-1/2 h-16 w-16 -translate-x-1/2 -translate-y-1/2 cursor-pointer drop-shadow-md"
              />
              <span className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white">
                {video.name}
              </span>
            </div>
          )}{" "}
          <Title type="TABLET" data={titleData} />
          <div className="w-full pt-5">
            <div className="w-full px-5">
              {isBuyable ? (
                <>
                  <div className="flex items-center md:justify-between">
                    {isFree ? (
                      <div>رایگان</div>
                    ) : (
                      <>
                        <span className="ml-3 md:m-0">
                          قیمت دوره{" "}
                          {discount !== null && discount !== 0
                            ? commafy(discount)
                            : commafy(price)}
                          تومان
                        </span>
                        {discount !== null && discount !== 0 && (
                          <span className="text-sm text-gray-450 line-through">
                            {price && commafy(price)} تومان
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  {allowSnapPay && !isFree ? (
                    <>
                      {!isLoadingForValidSnapp && isSnapPayAvailable ? (
                        <>
                          <hr className="mt-4" />
                          <div className="my-4 mx-auto flex w-fit items-center justify-center gap-2">
                            <div className="shrink-0">
                              <SnappPayIcon className="hidden w-[32px] lg:inline-block lg:w-[42px]" />
                              <SnappPayMiniIcon className="inline-block w-[32px] lg:hidden" />
                            </div>

                            <div>
                              <p className="text-[1a1c23] text-sm font-bold lg:text-base">
                                هر قسط با اسنپ‌پی:
                                {(() => {
                                  const amount = parseSnappPayInstallmentAmount(
                                    isSnapPayAvailable?.text
                                  );
                                  return amount
                                    ? ` ${commafy(amount)} تومان`
                                    : "";
                                })()}
                              </p>
                              <p className="mt-1 text-xs text-[#616475] lg:text-sm">
                                چهارقسط ماهانه، بدون سود، چک و ضامن
                              </p>
                            </div>
                          </div>
                        </>
                      ) : undefined}
                    </>
                  ) : undefined}
                </>
              ) : (
                <div className="text-sm">
                  {courseType === 2 ? (
                    <>
                      مخاطب گرامی، برای مشاهده این دوره لازم است بسته کامل را
                      تهیه نمائید. در صورت نیاز به راهنمایی با شماره{" "}
                      <a
                        href="tel:02175323000"
                        className="inline-block font-bold"
                        style={{ direction: "ltr" }}
                      >
                        021 753 23 000
                      </a>{" "}
                      تماس بگیرید.
                    </>
                  ) : (
                    <>{badge}</>
                  )}
                </div>
              )}{" "}
              <>
                <Button
                  disabled={!isBuyable}
                  isLoading={isLoading}
                  onClick={handlePay}
                  className="mt-10 w-full rounded bg-blue-550 py-2 text-white disabled:bg-gray-400 md:py-4"
                >
                  {isFree ? "افزودن به داشبورد" : "افزودن به سبد خرید"}
                </Button>
              </>
              <div
                className="my-7 h-px w-full"
                style={{
                  background:
                    "linear-gradient(270deg, rgba(54, 100, 148, 0.8) 0%, rgba(4, 128, 212, 0.8) 50%, rgba(39, 178, 188, 0.8) 100%)",
                }}
              />
            </div>
            <div className="flex flex-col">
              <h3 className="mb-5 px-5 text-base text-blue-550">
                ویژگی‌های دوره
              </h3>
              <div className="w-full md:px-5">
                <div
                  className={`grid aspect-[3/0] grid-cols-1 gap-2  ${
                    certificate?.hasCertificate
                      ? "md:aspect-[2/2] md:grid-cols-2 md:gap-5"
                      : "text-xs md:aspect-[3/1] md:grid-cols-3 md:gap-2"
                  }`}
                >
                  <div
                    className="relative md:rounded"
                    style={{ contentVisibility: "auto" }}
                  >
                    <img
                      loading="lazy"
                      src={"/images/detail/property/one.png"}
                      className="h-full w-full"
                      alt="تاریخ دوره"
                    />
                    <div
                      className="absolute top-0 left-0 flex h-full w-full flex-col items-center justify-center space-y-5 text-white xl:space-y-3"
                      style={{
                        background:
                          "linear-gradient(270deg, rgba(54, 100, 148, 0.8) 0%, rgba(4, 128, 212, 0.8) 50%, rgba(39, 178, 188, 0.8) 100%)",
                      }}
                    >
                      <Calender
                        className={
                          certificate.hasCertificate ? "h-9 w-9" : "h-6 w-6"
                        }
                      />
                      <span>
                        تاریخ{" "}
                        {getDateByData(date).type == "date" ? "دوره" : "انتشار"}
                      </span>
                      <span
                        className={`text-center ${
                          certificate.hasCertificate ? "text-sm" : "xl:text-xs"
                        }`}
                      >
                        {" "}
                        {getDateByData(date)?.data}
                      </span>
                    </div>
                  </div>
                  <div
                    className="relative md:rounded"
                    style={{ contentVisibility: "auto" }}
                  >
                    <img
                      loading="lazy"
                      src={"/images/detail/property/two.png"}
                      className={"h-full w-full"}
                      alt="مدت دوره"
                    />
                    <div
                      // style={{
                      //     background:
                      //         'linear-gradient(270deg, rgba(230, 88, 0, 0.8) 0%, rgba(227, 128, 30, 0.8) 50%, rgba(254, 189, 23, 0.8) 100%)',
                      // }}
                      className="absolute top-0 left-0 flex h-full w-full flex-col items-center justify-center space-y-5 text-white xl:space-y-3"
                    >
                      <Hourglass
                        className={
                          certificate.hasCertificate ? "h-9 w-9" : "h-6 w-6"
                        }
                      />
                      <span>مدت دوره</span>
                      <span
                        className={`text-center ${
                          certificate.hasCertificate ? "text-sm" : "xl:text-xs"
                        }`}
                      >
                        {courseDuration}
                      </span>
                    </div>
                  </div>
                  {certificate?.hasCertificate && (
                    <div
                      className="relative md:rounded"
                      style={{ contentVisibility: "auto" }}
                    >
                      <img
                        loading="lazy"
                        src={"/images/detail/property/three.png"}
                        className="h-full w-full object-cover"
                        alt="اعطای گواهی معتبر از دانشگاه تهران"
                      />
                      <div
                        // style={{
                        //     background:
                        //         'linear-gradient(270deg, rgba(79, 155, 10, 0.8) 0%, rgba(120, 177, 66, 0.8) 100%)',
                        // }}
                        className="absolute top-0 left-0 flex h-full w-full flex-col items-center justify-center space-y-5 text-white xl:space-y-3"
                      >
                        <Degree
                          className={
                            certificate.hasCertificate ? "h-9 w-9" : "h-6 w-6"
                          }
                        />
                        <span>با ارائه مدرک</span>
                        <span
                          className={`text-center ${
                            certificate.hasCertificate ? "text-sm" : "text-xs"
                          }`}
                        >
                          {certificate?.certificateTitle}
                        </span>
                      </div>
                    </div>
                  )}
                  <div
                    className="relative md:rounded"
                    style={{ contentVisibility: "auto" }}
                  >
                    <img
                      loading="lazy"
                      src={"/images/detail/property/four.png"}
                      className="h-full w-full"
                      alt="برگزاری دوره به صورت آنلاین"
                    />
                    <div
                      style={{
                        background:
                          "linear-gradient(270deg, rgba(60, 20, 154, 0.77) 0%, rgba(152, 19, 179, 0.54) 50%, rgba(208, 63, 178, 0.57) 100%)",
                      }}
                      className="absolute top-0 left-0 flex h-full w-full flex-col items-center justify-center space-y-5 text-white xl:space-y-3"
                    >
                      <Progress
                        className={
                          certificate.hasCertificate ? "h-9 w-9" : "h-6 w-6"
                        }
                      />
                      <span className="text-center">برگزاری دوره به صورت</span>
                      <span
                        className={`text-center ${
                          certificate.hasCertificate ? "text-sm" : "xl:text-xs"
                        }`}
                      >
                        {holdingType}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="w-full px-5">
                {/* <div
                                    className="my-7 h-px w-full"
                                    style={{
                                        background:
                                            'linear-gradient(270deg, rgba(54, 100, 148, 0.8) 0%, rgba(4, 128, 212, 0.8) 50%, rgba(39, 178, 188, 0.8) 100%)',
                                    }}
                                /> */}
                {extraDescription && (
                  <>
                    <div
                      className={style.customList}
                      dangerouslySetInnerHTML={{ __html: extraDescription }}
                    />
                    <div
                      className="my-7 h-px w-full text-justify"
                      style={{
                        background:
                          "linear-gradient(270deg, rgba(54, 100, 148, 0.8) 0%, rgba(4, 128, 212, 0.8) 50%, rgba(39, 178, 188, 0.8) 100%)",
                      }}
                    />
                  </>
                )}

                {/* <div className="flex flex-col">
                                    <span className="mb-5 text-base text-blue-550">انجمن</span>
                                    <div className="flex">
                                        <Conversation className="ml-5" />
                                        <div className="flex flex-col justify-around">
                                            <span className="text-sm md:text-base">
                                                گفتگو با دوستان در دادبانان
                                            </span>
                                            <span className="flex items-center text-xs text-blue-550 md:text-sm">
                                                کلیک کن و وارد شو
                                                <FillArrow className="mr-2" />
                                            </span>
                                        </div>
                                    </div>
                                </div> */}
              </div>
            </div>
          </div>
        </div>
        {image && (
          <img
            loading="lazy"
            style={{ objectPosition: "top" }}
            src={image}
            className="my-5 h-auto w-full object-contain"
            alt={imageAlternateText ?? "دوره"}
          />
        )}
      </div>
    </>
  );
};

export default Index;
